import { useState, useRef, useCallback, type DragEvent } from 'react';
import { Dialog } from '@/components/common/Dialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import {
  Upload,
  FileText,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Check,
} from 'lucide-react';
import { getAuthToken, rpc, formatTime } from '@/api/client';

/* ── Types matching the backend response ──────────────────────────── */

interface ParsedDriver {
  firstName: string;
  lastName: string;
  nationality: string;
}

interface ParsedEntry {
  driver: ParsedDriver;
  kartNumber: number;
  gridPosition: number;
  finishPosition: number;
  bestLapMs: number;
  totalTimeMs: number;
  lapsCompleted: number;
  penaltySeconds: number;
  penaltyReason: string;
  status: string;
}

interface ParsedSeason {
  id: string;
  name: string;
  year: number;
  country: string;
}

interface ParsedCategory {
  id: string;
  name: string;
  slug: string;
}

interface ParsedEvent {
  id: string;
  roundNumber: number;
  trackName: string;
  country: string;
  date: string;
  sourcePdf: string;
}

interface ParsedSessionMeta {
  id: string;
  sessionType: string;
  sessionLabel: string;
  totalLaps: number;
  time: string;
}

interface ParsedSession {
  season: ParsedSeason;
  category: ParsedCategory;
  event: ParsedEvent;
  session: ParsedSessionMeta;
  entries: ParsedEntry[];
  existsInDb: boolean;
  modified: boolean;
}

interface ParsedFileResult {
  sourceFile: string;
  sessions: ParsedSession[] | null;
  parseError: string | null;
}

interface ParseRacePDFsResponse {
  results: ParsedFileResult[];
}

interface SubmitResponse {
  imported: number;
  skipped: number;
  errors: number;
}

/* ── Component ────────────────────────────────────────────────────── */

interface Props {
  onClose: () => void;
  onComplete: () => void;
}

type Mode = 'upload' | 'parsing' | 'review' | 'submitting' | 'summary';

export function ImportRacePDFsDialog({ onClose, onComplete }: Props) {
  const { showToast } = useToast();
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('upload');
  const [progress, setProgress] = useState(0);

  // Upload stage
  const [isDragging, setIsDragging] = useState(false);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // Review stage
  const [parsedResults, setParsedResults] = useState<ParsedFileResult[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  // Summary stage
  const [summary, setSummary] = useState<SubmitResponse | null>(null);

  /* ── Drag & drop ─────────────────────────────────────────────── */

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const pdfs = Array.from(files).filter(f =>
      f.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfs.length === 0) {
      showToast(t('import.onlyPdfAccepted'), 'error');
      return;
    }

    // Buffer files into memory to prevent stale content URIs on mobile.
    const buffered = await Promise.all(
      pdfs.map(async (f) => {
        try {
          const buf = await f.arrayBuffer();
          return new File([buf], f.name, { type: f.type || 'application/pdf' });
        } catch {
          return f;
        }
      })
    );

    setPdfFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const novel = buffered.filter(f => !existing.has(f.name + f.size));
      return [...prev, ...novel];
    });
  }, [showToast, t]);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== index));
  };

  /* ── Parse PDFs ──────────────────────────────────────────────── */

  const startParsing = async () => {
    if (pdfFiles.length === 0) return;
    setMode('parsing');
    setProgress(0);

    const formData = new FormData();
    pdfFiles.forEach(f => formData.append('files', f));

    try {
      const xhr = new XMLHttpRequest();
      const token = getAuthToken();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      const result = await new Promise<ParseRacePDFsResponse>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error(t('import.parseFailed')));
            }
          } else {
            try {
              const data = JSON.parse(xhr.responseText);
              reject(new Error(data.error || t('import.uploadFailed')));
            } catch {
              reject(new Error(t('import.uploadFailed')));
            }
          }
        });

        xhr.addEventListener('error', () => reject(new Error(t('import.connectionLost'))));
        xhr.addEventListener('abort', () => reject(new Error(t('import.uploadAborted'))));

        xhr.open('POST', '/rpc/ParseRacePDFs');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      setParsedResults(result.results);

      // Pre-select all successfully parsed sessions.
      const allIds = new Set<string>();
      for (const file of result.results) {
        if (file.sessions) {
          for (const s of file.sessions) {
            allIds.add(s.session.id);
          }
        }
      }
      setSelectedSessions(allIds);
      setMode('review');
    } catch (e) {
      const err = e as Error;
      showToast(err.message, 'error');
      setMode('upload');
    }
  };

  /* ── Submit sessions ─────────────────────────────────────────── */

  const submitSessions = async () => {
    const sessionsToSubmit: ParsedSession[] = [];
    for (const file of parsedResults) {
      if (file.sessions) {
        for (const s of file.sessions) {
          if (selectedSessions.has(s.session.id)) {
            sessionsToSubmit.push(s);
          }
        }
      }
    }

    if (sessionsToSubmit.length === 0) return;

    setMode('submitting');
    try {
      const result = await rpc<SubmitResponse>('SubmitRaceSessions', {
        sessions: sessionsToSubmit,
      });
      setSummary(result);
      setMode('summary');
      if (result.imported > 0) {
        showToast(t('import.importedCount', { n: result.imported }));
      }
    } catch (e) {
      const err = e as Error;
      showToast(err.message, 'error');
      setSummary({ imported: 0, skipped: 0, errors: 1 });
      setMode('summary');
    }
  };

  /* ── Selection helpers ───────────────────────────────────────── */

  const allSessionIds = parsedResults
    .flatMap(f => f.sessions ?? [])
    .map(s => s.session.id);

  const allSelected = allSessionIds.length > 0 && allSessionIds.every(id => selectedSessions.has(id));

  const toggleSession = (id: string) => {
    setSelectedSessions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(allSessionIds));
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ── Close handler ───────────────────────────────────────────── */

  const handleClose = () => {
    if (summary && summary.imported > 0) {
      onComplete();
    } else {
      onClose();
    }
  };

  const isInProgress = mode === 'parsing' || mode === 'submitting';

  const selectedCount = selectedSessions.size;

  /* ── Footer ──────────────────────────────────────────────────── */

  let footer: React.ReactNode = null;

  if (mode === 'upload') {
    footer = (
      <>
        <button
          onClick={startParsing}
          disabled={pdfFiles.length === 0}
          className={`inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium
            transition-all cursor-pointer
            ${pdfFiles.length > 0
              ? 'bg-accent text-white hover:bg-accent/90'
              : 'bg-bg-raised text-text-muted cursor-not-allowed'
            }`}
        >
          <Upload size={14} />
          {t('import.upload')} {pdfFiles.length > 0 ? `(${pdfFiles.length})` : ''}
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
            bg-transparent text-text-secondary border border-border-default
            hover:text-text-primary transition-colors cursor-pointer"
        >
          {t('common.cancel')}
        </button>
      </>
    );
  } else if (mode === 'review') {
    footer = (
      <>
        <button
          onClick={submitSessions}
          disabled={selectedCount === 0}
          className={`inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium
            transition-all cursor-pointer
            ${selectedCount > 0
              ? 'bg-accent text-white hover:bg-accent/90'
              : 'bg-bg-raised text-text-muted cursor-not-allowed'
            }`}
        >
          <Check size={14} />
          {t('import.submit')} {selectedCount > 0 ? `(${selectedCount})` : ''}
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
            bg-transparent text-text-secondary border border-border-default
            hover:text-text-primary transition-colors cursor-pointer"
        >
          {t('common.cancel')}
        </button>
      </>
    );
  } else if (mode === 'summary') {
    footer = (
      <button
        onClick={handleClose}
        className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium
          bg-accent text-white hover:bg-accent/90 transition-all cursor-pointer"
      >
        {t('common.close')}
      </button>
    );
  }

  /* ── Render ──────────────────────────────────────────────────── */

  return (
    <Dialog
      open
      onClose={handleClose}
      title={t('import.title')}
      preventClose={isInProgress}
      footer={footer}
    >
      {/* ── Upload stage ── */}
      {mode === 'upload' && (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary mb-4">
            {t('import.uploadDesc')}
          </p>

          <div
            className={`drop-zone ${isDragging ? 'drop-zone--active' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInput}
              className="hidden"
              multiple
              accept=".pdf,application/pdf"
            />
            <FileText size={32} className="text-accent mb-3 opacity-80" />
            <div className="font-medium text-sm text-text-primary mb-1">
              {t('import.dragDropPdf')}
            </div>
            <div className="text-xs text-text-muted">
              {t('import.orBrowse')}{' '}
              <span className="text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent transition-all">
                {t('import.browseFiles')}
              </span>
            </div>
          </div>

          {pdfFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold tracking-wide text-text-muted uppercase mb-2">
                {t('import.filesSelected', { n: pdfFiles.length })}
              </h4>
              <div className="space-y-1">
                {pdfFiles.map((file, i) => (
                  <div key={`${file.name}-${i}`} className="flex items-center justify-between p-2 rounded-md bg-bg-raised border border-border-subtle group text-sm">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileText size={14} className="text-text-muted shrink-0" />
                      <span className="truncate text-text-primary font-mono text-xs">{file.name}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                      className="p-1 rounded text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Parsing progress ── */}
      {mode === 'parsing' && (
        <div className="py-8 text-center space-y-6">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto text-accent animate-pulse">
            <Upload size={24} className="animate-bounce" />
          </div>
          <div>
            <div className="text-base font-semibold text-text-primary mb-1">
              {t('import.uploadingPct', { n: progress })}
            </div>
          </div>
          <div className="w-full max-w-sm mx-auto h-2 bg-bg-raised border border-border-subtle rounded-full overflow-hidden">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Review stage ── */}
      {mode === 'review' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-secondary">
              {t('import.reviewDesc')}
            </p>
            <button
              onClick={toggleAll}
              className="text-xs text-accent hover:text-accent-hover transition-colors cursor-pointer shrink-0 ml-4"
            >
              {allSelected ? t('import.deselectAll') : t('import.selectAll')}
            </button>
          </div>

          {parsedResults.map((file) => (
            <div key={file.sourceFile} className="space-y-2">
              {/* File header */}
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-text-muted shrink-0" />
                <span className="text-xs font-mono text-text-secondary truncate">{file.sourceFile}</span>
              </div>

              {/* Parse error */}
              {file.parseError && (
                <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20 flex gap-2 text-xs">
                  <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
                  <span className="text-red-300">{file.parseError}</span>
                </div>
              )}

              {/* Parsed sessions */}
              {file.sessions && file.sessions.map((s) => {
                const isSelected = selectedSessions.has(s.session.id);
                const isExpanded = expandedSessions.has(s.session.id);
                return (
                  <div
                    key={s.session.id}
                    className={`rounded-md border transition-colors ${
                      isSelected
                        ? 'bg-bg-raised border-border-default'
                        : 'bg-bg-surface/50 border-border-subtle opacity-60'
                    }`}
                  >
                    {/* Session row */}
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleSession(s.session.id)}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-accent border-accent'
                            : 'border-border-default hover:border-border-hover'
                        }`}
                      >
                        {isSelected && <Check size={10} className="text-white" />}
                      </button>

                      {/* Session info */}
                      <button
                        onClick={() => toggleExpanded(s.session.id)}
                        className="flex-1 flex items-center gap-3 min-w-0 text-left cursor-pointer"
                      >
                        <ChevronRight
                          size={12}
                          className={`text-text-muted transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-text-primary">{s.event.trackName}</span>
                            <span className="inline-block px-2 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-[0.65rem] font-medium text-accent-hover">
                              {s.session.sessionLabel || s.session.sessionType}
                            </span>
                            {s.existsInDb && (
                              <span className="inline-block px-2 py-0.5 bg-yellow/10 border border-yellow/20 rounded-full text-[0.65rem] font-medium text-yellow">
                                {t('import.existsInDb')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-text-muted">
                            <span>{s.event.date}</span>
                            <span>·</span>
                            <span>{s.category.name}</span>
                            <span>·</span>
                            <span>{s.season.name} {s.season.year}</span>
                            <span>·</span>
                            <span>{t('import.entriesCount', { n: s.entries.length })}</span>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Expanded entries table */}
                    {isExpanded && s.entries.length > 0 && (
                      <div className="border-t border-border-subtle">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="text-[0.7rem] uppercase tracking-wider text-text-muted divider">
                              <th className="text-left px-3 py-2 font-semibold">{t('races.position')}</th>
                              <th className="text-left px-3 py-2 font-semibold">{t('races.kart')}</th>
                              <th className="text-left px-3 py-2 font-semibold">{t('races.driver')}</th>
                              <th className="text-right px-3 py-2 font-semibold">{t('races.bestLap')}</th>
                              <th className="text-right px-3 py-2 font-semibold">{t('races.laps')}</th>
                              <th className="text-left px-3 py-2 font-semibold">{t('races.status')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s.entries.map((entry, ei) => (
                              <tr
                                key={ei}
                                className={`hover:bg-bg-hover transition-colors ${ei < s.entries.length - 1 ? 'divider' : ''}`}
                              >
                                <td className="px-3 py-2 text-text-secondary tabular-nums mono">{entry.finishPosition}</td>
                                <td className="px-3 py-2 text-text-muted tabular-nums mono">{entry.kartNumber || '—'}</td>
                                <td className="px-3 py-2 text-text-primary">
                                  {entry.driver.firstName} {entry.driver.lastName}
                                  {entry.driver.nationality && (
                                    <span className="text-text-muted ml-1">({entry.driver.nationality})</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right text-text-secondary mono tabular-nums">
                                  {formatTime(entry.bestLapMs)}
                                </td>
                                <td className="px-3 py-2 text-right text-text-muted tabular-nums mono">{entry.lapsCompleted}</td>
                                <td className="px-3 py-2 text-text-muted">{entry.status || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ── Submitting ── */}
      {mode === 'submitting' && (
        <div className="py-8 text-center space-y-6">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto text-accent animate-pulse">
            <Upload size={24} className="animate-bounce" />
          </div>
          <div className="text-base font-semibold text-text-primary">
            {t('import.submitting')}
          </div>
        </div>
      )}

      {/* ── Summary ── */}
      {mode === 'summary' && summary && (
        <div className="space-y-6">
          {summary.imported > 0 ? (
            <div className="p-4 rounded-md bg-green-500/10 border border-green-500/20 flex gap-3 text-green-500">
              <CheckCircle2 className="shrink-0" size={20} />
              <div className="text-sm">
                <div className="font-medium mb-0.5">{t('import.successMessage')}</div>
              </div>
            </div>
          ) : summary.errors === 0 ? (
            <div className="p-4 rounded-md bg-blue-500/10 border border-blue-500/20 flex gap-3 text-blue-400">
              <CheckCircle2 className="shrink-0" size={20} />
              <div className="text-sm">
                <div className="font-medium mb-0.5">{t('import.noNewSessions')}</div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
              <div className="text-2xl font-bold text-green-500 mb-1">{summary.imported}</div>
              <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.imported')}</div>
            </div>
            <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
              <div className="text-2xl font-bold text-text-secondary mb-1">{summary.skipped}</div>
              <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.skipped')}</div>
            </div>
            <div className="p-4 rounded-md bg-bg-raised border border-border-subtle text-center">
              <div className={`text-2xl font-bold mb-1 ${summary.errors > 0 ? 'text-red-500' : 'text-text-secondary'}`}>
                {summary.errors}
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wide font-medium">{t('import.errors')}</div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
